import { getTunedImageForPlate } from 'components/commandpanel/ImageTuning/ImageTuningManager';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Image } from 'react-konva';
import TileSize, { GetDisplayMode, DisplayModes } from 'utils/Global';
import useRockFetchGet from 'utils/RockFetch';
import withPerf from 'components/customComponents/withPerf';
import { useRMSelector } from 'redux/hooks';
import { selectAutoLevelingModeOn } from 'redux/selectors/StateSelectors';
import {
  selectExp,
  selectExperimentPlates,
} from 'redux/selectors/ExperimentStateSelector';
import { PlateRendererProps, WellDetailsStates } from 'business/models/CustomModels';
import {
  useLastSelectedWellNavigationDetails,
  useSelectedDropImageParameters,
  useTuningAppliedWellNumbers,
} from 'redux/hooks/experiment';
import { useShowOverrideTuning, useWellDropImageMetaData } from 'redux/hooks/well';
import {
  getCroppedImages,
  getTunedImages,
} from 'components/commandpanel/ImageTuning/utils';
import { debounce } from 'lodash';
import { useStore } from 'react-redux';
import { RootState } from 'redux/configureStore';
import { useUpdateEffect } from 'CustomHooks/useUpdateEffect';

const tileImages = {}; //Create2DArray(2);

let tileDrawableImages = {}; //Create2DArray(2);
let tileOverrideDrawableImages = {}; //Create2DArray(2);
let tileOverrideSourceImages = {}; // Merged per-well override image before plate tuning.

function TilePainter(props: PlateRendererProps) {
  const [tiles, setTiles] = useState([]);

  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const [rockFetchGet] = useRockFetchGet();
  const loadedImgNums = useRef<Record<string, boolean>>({});
  const wellDropMetaDeta = useWellDropImageMetaData();
  const plates = useRMSelector(selectExperimentPlates);
  const plate = plates.find((p) => p.plateNumber === props.plateNumber);
  const exp = useRMSelector(selectExp);
  const level = !props.zoomLevel ? null : props.zoomLevel - 1;
  const stx = plate.stx;
  const sty = plate.sty;
  const endx = plate.endx;
  const endy = plate.endy;
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevAbortParams = useRef({
    imageBatchId: null,
    cpId: null,
    dropNumber: null,
    regionName: null,
  });

  const platesize = plate.rows * plate.cols;
  const [refresh, setRefresh] = useState(false);

  function getWidthheight(level) {
    if (level !== null) {
      return {
        tw: TileSize[platesize][level][0],
        th: TileSize[platesize][level][1],
      };
    }
  }

  const autoLevelingModeOn = useRMSelector(selectAutoLevelingModeOn);
  const getTuningAppliedWellNumbers = useTuningAppliedWellNumbers();
  const isShowOverride = useShowOverrideTuning();
  const { inspection, cpId, dropNumber, regionName } = selectedDropImageParameters || {};
  const imageBatchId = inspection?.groupedInspections?.find(
    (gi) => gi.plateId === plate.plateId,
  )?.imageBatchId;
  const store = useStore<RootState>();

  const getTileKey = (level: number, tileNum: number) => {
    return `${plate.plateId}-${level}-${tileNum}-${imageBatchId}-${dropNumber}-${cpId}-${regionName}`;
  };

  const tuningValuesForPlate = useMemo(() => {
    const tuningValues =
      store?.getState()?.experiment?.expData?.experimentTuningValues ?? [];

    if (cpId == null) {
      return tuningValues ?? [];
    }
    const selectedTuningValue = tuningValues.find((t) => t.captureProfileID === cpId);
    if (selectedTuningValue) {
      return [selectedTuningValue];
    }
    return [];
  }, [cpId, store?.getState()?.experiment?.expData?.experimentTuningValues]);

  useEffect(() => {
    const { layerRef } = props.canvasRefs();
    layerRef?.batchDraw();
  }, []);

  const getTileRange = (
    levelToUse: number,
    platesizeToUse: number,
    startX: number,
    endX: number,
    startY: number,
    endY: number,
  ) => {
    const c1 = Math.floor(
      startX / (exp.containerCols / TileSize[platesizeToUse][levelToUse][0]),
    );
    const c2 = Math.ceil(
      endX / (exp.containerCols / TileSize[platesizeToUse][levelToUse][0]),
    );
    const r1 = Math.floor(
      startY / (exp.containerRows / TileSize[platesizeToUse][levelToUse][1]),
    );
    const r2 = Math.ceil(
      endY / (exp.containerRows / TileSize[platesizeToUse][levelToUse][1]),
    );

    return { c1, c2, r1, r2 };
  };

  const retuneLoadedTiles = async () => {
    if (!wellNavigationDetails) {
      return;
    }
    if (
      GetDisplayMode() !== DisplayModes.IMAGE &&
      GetDisplayMode() !== DisplayModes.IMAGESCORE
    ) {
      return;
    }

    for (const levelToRetune of [0, 1]) {
      const [tileColumns, tileRows] = TileSize[platesize][levelToRetune];
      const tileCount = tileColumns * tileRows;

      for (let tilenum = 0; tilenum < tileCount; tilenum++) {
        const key = getTileKey(levelToRetune, tilenum);

        // Tiles outside the viewport are loaded lazily and will use the latest values when
        // they are fetched.
        if (!tileImages[key]) {
          continue;
        }

        const tuned = await getTunedDrawableImage(levelToRetune, tilenum);
        if (tuned) {
          tileDrawableImages[key] = tuned.plateImage;
          tileOverrideDrawableImages[key] = tuned.plateOverRideImage;
        }
      }
    }

    refreshTiles();
  };

  useEffect(() => {
    const updateTunedTiles = async () => {
      await retuneLoadedTiles();
    };

    updateTunedTiles();
  }, [tuningValuesForPlate, autoLevelingModeOn]);

  useEffect(() => {
    refreshTiles();
  }, [wellNavigationDetails, imageBatchId, dropNumber, regionName, cpId, isShowOverride]);

  useUpdateEffect(
    () => {
      tileOverrideDrawableImages = {};
      tileDrawableImages = {};
      tileOverrideSourceImages = {};
    },
    [plate?.width],
    false,
  );

  useEffect(() => {
    // Drop-level tuning metadata changes require rebuilding composed override tiles.
    tileOverrideSourceImages = {};
  }, [wellDropMetaDeta]);

  async function loaderAsync(apiUrl, tilenum, lev, abortSignal?) {
    let url = null;
    try {
      url = await rockFetchGet(apiUrl, 'get', undefined, true, abortSignal);
    } catch (error) {
      if (abortSignal?.aborted) {
        return;
      }
      throw error;
    }
    if (url === null) {
      return null;
    }

    const img = new window.Image();
    img.src = url.tileFileStr;
    img.onload = async () => {
      if (abortSignal?.aborted) {
        return;
      }
      const key = getTileKey(lev, tilenum);

      tileImages[key] = img;

      const { plateImage, plateOverRideImage } =
        tileDrawableImages[key] && tileOverrideDrawableImages[key]
          ? {
              plateImage: tileDrawableImages[key],
              plateOverRideImage: tileOverrideDrawableImages[key],
            }
          : await getTunedDrawableImage(lev, tilenum);
      if (abortSignal?.aborted) {
        return;
      }
      if (plateImage && plateOverRideImage) {
        tileDrawableImages[key] = plateImage;
        tileOverrideDrawableImages[key] = plateOverRideImage;
        loadedImgNums.current[key] = true;
        refreshTiles();
      }
    };
  }

  const loadTiles = async (level, platesize, stx, endx, sty, endy) => {
    if (!wellNavigationDetails || level == null) {
      return null;
    }
    // Tile painter called in design view too as props were getting changed
    if (
      GetDisplayMode() !== DisplayModes.IMAGE &&
      GetDisplayMode() !== DisplayModes.IMAGESCORE
    ) {
      return;
    }

    const { c1, c2, r1, r2 } = getTileRange(level, platesize, stx, endx, sty, endy);
    for (let c = c1; c < c2; c++) {
      for (let r = r1; r < r2; r++) {
        const tilenum = r * TileSize[platesize][level][0] + c;

        const key = getTileKey(level, tilenum);
        if (loadedImgNums.current[key]) {
          continue;
        }
        const apiUrl = `api/Plate/${plate.plateId}/${
          level + 1
        }/${imageBatchId}/${dropNumber}/${cpId}/${regionName}/${tilenum}/${
          TileSize[platesize][0][0]
        }/${TileSize[platesize][0][0]}/${plate.rows}/${plate.cols}`;
        loaderAsync(apiUrl, tilenum, level, abortControllerRef.current?.signal);
      }
    }
  };

  const debouncedLoadTiles = useMemo(
    () =>
      debounce((level, platesize, stx, endx, sty, endy) => {
        loadTiles(level, platesize, stx, endx, sty, endy);
      }, 200),
    [wellNavigationDetails, selectedDropImageParameters],
  );

  useEffect(() => {
    if (!imageBatchId) {
      return;
    }

    const paramsChanged =
      prevAbortParams.current.imageBatchId !== imageBatchId ||
      prevAbortParams.current.cpId !== cpId ||
      prevAbortParams.current.dropNumber !== dropNumber ||
      prevAbortParams.current.regionName !== regionName;

    if (paramsChanged) {
      debouncedLoadTiles.cancel();
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
    }

    if (!abortControllerRef.current) {
      abortControllerRef.current = new AbortController();
    }

    loadTiles(level, platesize, stx, endx, sty, endy);

    prevAbortParams.current = {
      imageBatchId,
      cpId,
      dropNumber,
      regionName,
    };
  }, [
    level,
    platesize,
    stx,
    endx,
    sty,
    endy,
    imageBatchId,
    cpId,
    dropNumber,
    regionName,
    plate?.width,
  ]);

  function getImage(tileImage) {
    const tunedImage = getTunedImageForPlate(
      tuningValuesForPlate,
      tileImage,
      cpId,
      autoLevelingModeOn,
    );

    return tunedImage === undefined ? tileImage : tunedImage;
  }

  function loadImageAsync(img) {
    return new Promise((resolve) => {
      if (img.complete) {
        resolve(img);
        return;
      }
      img.onload = () => resolve(img);
    });
  }

  async function getTunedDrawableImage(level, index) {
    const { tw, th } = getWidthheight(level);
    const key = getTileKey(level, index);
    if (tileImages[key] !== undefined) {
      const tileImg = tileImages[key];
      const tileRect = {
        x: plate.startX + (index % tw) * (plate.width / tw),
        y: plate.startY + Math.floor(index / tw) * (plate.height / th),
        w: plate.width / tw,
        h: plate.height / th,
      };
      const overrideSourceImage =
        tileOverrideSourceImages[key] ??
        (await getTileTunedImages(tileImg, tileRect, plate.wellRects));
      if (overrideSourceImage) {
        tileOverrideSourceImages[key] = overrideSourceImage;
      }

      const tunedTileImage = getImage(tileImg);
      const tunedOverrideImage = overrideSourceImage
        ? getImage(overrideSourceImage)
        : null;

      await loadImageAsync(tunedTileImage);
      await loadImageAsync(tunedOverrideImage);
      const plateImage = (
        <Image
          id={`tilepainter-img-plateimage-${key}`}
          key={`image-${key}`}
          x={tileRect.x}
          y={tileRect.y}
          width={tileRect.w}
          height={tileRect.h}
          image={tunedTileImage}
          name="tile-image"
        />
      );
      const plateOverRideImage = (
        <Image
          id={`tilepainter-img-plateoverrideimage-${key}`}
          key={`image-${key}-override`}
          x={tileRect.x}
          y={tileRect.y}
          width={tileRect.w}
          height={tileRect.h}
          image={tunedOverrideImage ?? tunedTileImage}
          name="tile-image"
        />
      );
      return { plateImage, plateOverRideImage };
    }

    return null;
  }

  const getTileTunedImages = async (tileImg, tileRect, wellRects) => {
    const { wellNum, plateNum, plateID } = wellNavigationDetails || {};
    const { cpId, dropNumber, inspection, regionName } =
      selectedDropImageParameters || {};

    const wellDetails = {
      wellNum,
      plateNum,
      plateID,
      cpID: cpId,
      dropNumber,
      inspectionSet: inspection,
      regionName,
      imageBatchId: inspection?.groupedInspections?.find((gi) => gi.plateId === plateID)
        ?.imageBatchId,
    } as WellDetailsStates;

    const tuningAppliedWellNumbers = getTuningAppliedWellNumbers(wellDetails);
    const tileCoordinates = {
      startX: tileRect.x,
      startY: tileRect.y,
      plateWidth: tileRect.w,
      plateHeight: tileRect.h,
    };
    const croppedImages = await getCroppedImages(
      tileImg,
      tuningAppliedWellNumbers,
      plate.wellRects,
      tileCoordinates,
    );
    const tunedImages = await getTunedImages(
      tuningAppliedWellNumbers,
      croppedImages,
      wellDropMetaDeta,
      wellDetails,
    );
    return mergeTunedImagesIntoTile(tileImg, tileRect, wellRects, tunedImages);
  };

  const mergeTunedImagesIntoTile = (
    tileImg: HTMLImageElement,
    tileRect,
    wellRects,
    tunedImages: { [wellNum: number]: HTMLImageElement },
  ): HTMLImageElement => {
    // Create canvas same size as the original tile
    const tileWidth = tileImg.naturalWidth || tileImg.width;
    const tileHeight = tileImg.naturalHeight || tileImg.height;
    const canvas = document.createElement('canvas');
    canvas.width = tileWidth;
    canvas.height = tileHeight;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.imageSmoothingQuality = 'high';

    // Draw the original tile first
    ctx.drawImage(tileImg, 0, 0, tileWidth, tileHeight);

    const scaleX = tileWidth / tileRect.w;
    const scaleY = tileHeight / tileRect.h;

    // Draw each tuned well image back in place
    for (const wellNum in tunedImages) {
      const rect = wellRects[Number(wellNum) - 1];
      if (!rect) {
        continue;
      }

      const dx = (rect.x - tileRect.x) * scaleX;
      const dy = (rect.y - tileRect.y) * scaleY;
      const dw = rect.w * scaleX;
      const dh = rect.h * scaleY;

      ctx.drawImage(tunedImages[wellNum], dx, dy, dw, dh);
    }

    // Convert merged tile back to an image
    const mergedImg = new window.Image();
    mergedImg.src = canvas.toDataURL();
    return mergedImg;
  };

  function getRelevantTiles(level) {
    const tile = [];
    const limit = TileSize[platesize][level][0] * TileSize[platesize][level][1];
    const resultTiles = isShowOverride ? tileOverrideDrawableImages : tileDrawableImages;
    for (let index = 0; index < limit; index++) {
      if (resultTiles[getTileKey(level, index)]) {
        tile.push(resultTiles[getTileKey(level, index)]);
      }
    }

    return tile;
  }

  function refreshTiles() {
    setTiles([...getRelevantTiles(0), ...getRelevantTiles(1)]);
    return;
  }

  function drawTiles() {
    return tiles;
  }

  if (refresh) {
    setRefresh(false);
  }
  return drawTiles();
}

export default withPerf(TilePainter);
